<script setup lang="ts">
import { computed, inject, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import { onBeforeRouteUpdate, RouteLocation, RouterLink, useRoute, useRouter } from 'vue-router';
import { ArrowRightIcon, PlusIcon, UserGroupIcon } from 'tir-style-system/icons/outline';
import { SidebarMenuItem, SidebarSkeleton } from './components';
import {
  NamespaceModel,
  QueueTasksModel,
  GroupModel,
  UpdateQueueNameModel,
  DeleteQueueInfoModel,
  GroupCreateEventResponseModel,
  DeleteGroupEventResponseModel,
  GroupUpdateNameEventResponseModel,
} from './models';
import { AdapterService } from './adapter';
import { useSidebarTree } from './composables';
import { TreeItemType } from './enums';
import { projectInjectKey } from '@/inject-keys';
import { CreateNamespaceDrawer, CreateNamespaceResultModel } from '@/components';
import { DragModel } from '../tir-pm-kanban';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmScrollbar,
} from 'tir-components';
import RemoveStopperModal from './components/remove-stopper-modal.vue';
import { CurrentUser } from 'tir-pm-user-core';
import { ProjectModel } from '@/models';
import TirPmSkeleton from '../tasks-kanban/components/TirPmSkeleton/tir-pm-skeleton.vue';
import { CreateNamespaceInfo, DeleteNamespaceInfo, UpdateNamespaceInfo } from './types';
import { MonitoringMenu } from './components/monitoring';

const router = useRouter();

const adapter = new AdapterService();

/**Параметры */
const props = defineProps({
  /**ID очереди тикета */
  /**Статичное отображение сайдбара */
  isStatic: { type: Boolean, default: false },
  /**Ширина сайдбара */
  width: { type: Number },
  /**id проекта */
  projectProps: { type: ProjectModel, default: new ProjectModel() },
  /**Скрыть информацию о проекте */
  hideProjectInfo: { type: Boolean },
  /**Отображать только рабочие области */
  onlyNamespaces: { type: Boolean },
  /**Пропуск инициализации */
  skipInit: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Открытие рабочей области */
  (e: 'openNamespace', namespaceId: string): void;
  /**Открытие папки */
  (e: 'openGroup', namespaceId: string, groupId: number): void;
  /**Открытие очереди */
  (e: 'openQueue', namespaceId: string, queueId: string): void;
  (e: 'getQueueItems', sidebarItems: GroupModel);
}>();

/**Кол-во сотрудников в моей команде */
const myTeamCountEmployees = ref(0);

/**Есть элемент над которым выполняется какое либо действие */
const haveActionItem = ref(false);
const isLoading = ref(false);

watch(
  () => props.projectProps,
  async () => {
    isLoading.value = true;
    if (props.projectProps) {
      const resp = await adapter.getTree(Number(props.projectProps.Id), props.onlyNamespaces);
      isLoading.value = false;
      if (resp.IsSuccess) setSidebarItems(resp.Value);
      // emit("sidebarItems", sidebarItems);
    }
  },
);

/** Открыто ли меню */
const isSidebarOpen = ref(true);
/** Показывать только иконки */
const showOnlyIcons = ref(false);
/**Текущий перетаскиваемый элемент */
const currentDragging = ref<DragModel<GroupModel | QueueTasksModel>>();
provide('currentDragging', currentDragging);
/**Происходит ли перетаскивание */
const isDrag = ref(false);
provide('isDrag', isDrag);
/**Директория над которой перетаскивается элемент */
const currentDragOver = ref<NamespaceModel | GroupModel>();
provide('currentDragOver', currentDragOver);
/**Окно для отображения ошибки удаления элемента */
const $removeStopperModal = ref<InstanceType<typeof RemoveStopperModal>>();

/**rem единица */
const rem = computed(() => parseFloat(getComputedStyle(document.documentElement).fontSize));

/** Дефолтная ширина */
const defaultSidebarWidth = 72;
const defaultOpenSidebarWidth = 375;
const minWidthToOpenSidebar = defaultSidebarWidth + 80;

/** Настроенная ширина */
const updatedOpenSidebarWidth = ref(defaultOpenSidebarWidth);

/** Вычисляемая ширина sidebar */
const sidebarWidth = computed(() => {
  if (props.isStatic) return props.width;
  /** Когда sidebar открыт */
  if (isSidebarOpen.value) {
    /** Когда обновленная ширина больше ширины по дефолту */
    if (updatedOpenSidebarWidth.value > defaultOpenSidebarWidth) {
      return updatedOpenSidebarWidth.value;
    } else {
      /** Когда изменяется размер sidebar */
      if (isResizing.value) {
        return updatedOpenSidebarWidth.value;
      } else {
        return updatedOpenSidebarWidth.value > minWidthToOpenSidebar
          ? updatedOpenSidebarWidth.value
          : minWidthToOpenSidebar;
      }
    }
  } else {
    return defaultSidebarWidth;
  }
});

/** Изменяется ли ширина sidebar */
const isResizing = ref(false);

/**
 * Начало изменение ширины sidebar
 * @param event
 */
const startResize = (event: MouseEvent) => {
  if (!isSidebarOpen.value) {
    isSidebarOpen.value = true;

    if (event.clientX < defaultSidebarWidth + 50) {
      showOnlyIcons.value = true;
    } else {
      showOnlyIcons.value = false;
    }
  }

  isResizing.value = true;

  setSidebarWidth(event.clientX);

  /** Отключаем выделение */
  document.body.style.userSelect = 'none';

  /** Отслеживание перемещение стрелки по экрану */
  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', onFinish);

  /** Отключает transition у sidebar чтобы не было задержек */
  // @ts-ignore
  document.querySelector('.sidebar').style.transition = '0s width';
  // @ts-ignore
  document.querySelector('.sidebar__open-btn').style.transition = '0s left';
};
/**Получить rem значение */
const getRem = (px: number) => {
  if (!px) return 0;
  return px / rem.value;
};

/**
 * Изменение ширины sidebar
 * @param event
 */
const onResize = (event: MouseEvent) => {
  if (!isResizing.value) return;

  if (event.clientX > minWidthToOpenSidebar) {
    showOnlyIcons.value = false;
    setSidebarWidth(event.clientX);
  } else if (event.clientX > defaultSidebarWidth && event.clientX < minWidthToOpenSidebar) {
    showOnlyIcons.value = true;
    setSidebarWidth(event.clientX);
  } else {
    setSidebarWidth(defaultSidebarWidth);
  }
};
/**Установка ширины сайдбара */
const setSidebarWidth = (width: number) => {
  updatedOpenSidebarWidth.value = width;
  document.dispatchEvent(
    new CustomEvent('sidebar-resize', { detail: updatedOpenSidebarWidth.value }),
  );
};

/**
 * Завершение изменение ширины sidebar
 * @param event
 */
const onFinish = (event: MouseEvent) => {
  if (!isResizing.value) return;

  isResizing.value = false;

  document.body.style.userSelect = 'auto';

  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', onFinish);

  // @ts-ignore
  document.querySelector('.sidebar').style.transition = '0.2s width';
  // @ts-ignore
  document.querySelector('.sidebar__open-btn').style.transition = '0.3s left';

  if (event.clientX < minWidthToOpenSidebar) {
    isSidebarOpen.value = false;
    showOnlyIcons.value = true;
    setSidebarWidth(defaultSidebarWidth);
  }
};

/** Переключатель видимости sidebar */
const toggleSidebar = (_isSidebarOpen: boolean) => {
  isSidebarOpen.value = _isSidebarOpen;
  showOnlyIcons.value = !_isSidebarOpen;
  setSidebarWidth(sidebarWidth.value);
};

/** Переключатель кнопки отображения sidebar */
const toggleSidebarBtnVisibility = (showBtn: boolean) => {
  const btn = document.querySelector('.sidebar__open-btn');

  if (!btn) return;
  if (showBtn) {
    btn.classList.add('sidebar__open-btn_visible');
  } else {
    btn.classList.remove('sidebar__open-btn_visible');
  }
};

const {
  sidebarItems,
  setSidebarItems,
  openMenuItemFromRoute,
  removeItem,
  addMenuItem,
  updateItem,
  move,
} = useSidebarTree();

const project = inject(projectInjectKey);

const openProject = () => {
  router.push({ name: 'project' });
};

/**При создании неймспейса */
const onCreateNamespace = (data: CreateNamespaceInfo) => {
  const projectData = project.value ?? props.projectProps;
  if (data.ProjectId && projectData.Id !== data.ProjectId) return;

  // Добавление неймспейса в сайдбар
  addMenuItem(
    new NamespaceModel({
      Id: data.Id,
      Name: data.Name,
      IconId: data.IconId,
      IconUrl: data.IconUrl,
      ColorId: data.ColorId,
      Color: data.Color,
    }),
    true,
  );
};

/**При удалении неймспейса */
const onDeleteNamespace = (data: DeleteNamespaceInfo) => {
  removeItem(data.NamespaceId, TreeItemType.Namespace);
};

/**При обновлении неймспейса */
const onUpdateNamespace = (data: UpdateNamespaceInfo) => {
  const namespace = new NamespaceModel({
    Id: data.Id,
    Name: data.Name,
    IconId: data.IconId,
    IconUrl: data.IconUrl,
    ColorId: data.ColorId,
    Color: data.Color,
  });
  updateItem(namespace);
};

/**При обновлении очереди */
const onUpdateQueue = (data: UpdateQueueNameModel) => {
  const queue = new QueueTasksModel({
    Id: data.QueueId,
    Name: data.Name,
  });
  updateItem(queue);
};

/**При создании очереди */
const onCreateQueue = (queue: QueueTasksModel) => {
  addMenuItem(queue);
};
/**При удалении очереди */
const onDeleteQueue = (queue: DeleteQueueInfoModel) => {
  removeItem(queue.QueueId, TreeItemType.Queue);
};
/**При удалении папки */
const onDeleteGroup = (_group: DeleteGroupEventResponseModel) => {
  removeItem(_group.GroupId, TreeItemType.Group);
};
/**При обновлении папки */
const onUpdateGroup = (data: GroupUpdateNameEventResponseModel) => {
  const group = new GroupModel({
    Id: data.GroupId,
    Name: data.Name,
  });
  updateItem(group);
};
/**При создании папки */
const createGroup = (_group: GroupCreateEventResponseModel) => {
  const group = new GroupModel({
    Id: _group.GroupId,
    Name: _group.Name,
    NamespaceId: _group.NamespaceId,
    Number: _group.Number,
  });
  addMenuItem(group);
};

/**Перемещение элемента в другую директорию */
const onMove = () => {
  const item = currentDragging.value?.Data;
  if (!item) return;

  const parentId = currentDragOver.value.Id;

  if (item.isGroup()) {
    adapter.moveGroup(item.Id, parentId);
  } else if (item.isQueue()) {
    adapter.moveQueue(item.Id, parentId);
  }

  move(item, parentId);

  isDrag.value = false;
  currentDragging.value = undefined;
  currentDragOver.value = undefined;
};
/**Открыть окно редактирования очереди */
const onEditQueue = (queueId: string) => {};
/**Открыть окно предупреждающее о невозможности удаления элемента */
const openRemoveStopperModal = (type: TreeItemType) => {
  $removeStopperModal.value?.open(type);
};
/**Изменить данные рабочей области */
const changeNamespace = (_namespace: NamespaceModel) => {
  const namespace = sidebarItems.value.find((x) => x.isNamespace() && x.Id == _namespace.Id);
  if (namespace) {
    namespace.Name = _namespace.Name;
  }
};
/**Получение кол-во пользователей команды текущего пользователя  */
const getMyTeamCountEmployees = async () => {
  const resp = await adapter.getMyTeamCountEmployees();

  if (resp.IsSuccess) {
    myTeamCountEmployees.value = resp.Value;
  }
};
/**Получение дерева проекта */
const setProjectTree = async () => {
  isLoading.value = true;
  const resp = await adapter.getTree(Number(project.value.Id), props.onlyNamespaces);
  isLoading.value = false;
  if (resp.IsSuccess) setSidebarItems(resp.Value);
  // emit("sidebarItems", sidebarItems);
};
/**Отправка события для открытия рабочей области */
const openNamespace = (namespaceId: string, route: RouteLocation) => {
  emit('openNamespace', namespaceId);
  if (props.isStatic) openMenuItemFromRoute(route);
};
/**Отправка события для открытия папки */
const openGroup = (namespaceId: string, groupId: number, route: RouteLocation) => {
  emit('openGroup', namespaceId, groupId);
  if (props.isStatic) openMenuItemFromRoute(route);
};
/**Отправка события для открытия очереди */
const openQueue = (namespaceId: string, queueId: string, route: RouteLocation) => {
  emit('openQueue', namespaceId, queueId);
  if (props.isStatic) openMenuItemFromRoute(route);
};

/**Подписаться/отписаться на/от события */
const toggleEvents = (isSubscribe: boolean) => {
  const eventFnName = isSubscribe ? 'on' : 'removeListener';

  adapter.CreateNamespaceEvent[eventFnName](onCreateNamespace);
  adapter.DeleteNamespaceEvent[eventFnName](onDeleteNamespace);
  adapter.UpdateNamespaceEvent[eventFnName](onUpdateNamespace);
  adapter.UpdateQueueEvent[eventFnName](onUpdateQueue);
  adapter.DeleteQueueEvent[eventFnName](onDeleteQueue);
  adapter.CreateQueueEvent[eventFnName](onCreateQueue);
  adapter.OnCreateGroupEvent[eventFnName](createGroup);
  adapter.OnDeleteGroupEvent[eventFnName](onDeleteGroup);
  adapter.OnUpdateNameGroupEvent[eventFnName](onUpdateGroup);
};

const getData = async () => {
  try {
    if (!props.isStatic) {
      getMyTeamCountEmployees();
    }
    await setProjectTree();
    if (!props.isStatic) {
      openMenuItemFromRoute(router.currentRoute.value);
    }
  } catch (e) {
    console.error(e);
  }
};

/**Отслеживание изменения id проекта */
watch(
  () => project?.value?.Id,
  () => {
    getData();
  },
);

onMounted(async () => {
  try {
    if (!props.skipInit) {
      await getData();
    }
    toggleEvents(true);
  } catch (e) {
    console.error(e);
  }
  setTimeout(() => setSidebarWidth(defaultOpenSidebarWidth));
});

watch(
  () => router.currentRoute.value.path,
  () => {
    setTimeout(() => setSidebarWidth(defaultOpenSidebarWidth));
  },
);

onUnmounted(() => {
  toggleEvents(false);
});

const isProjectAdmin = computed(() => {
  const x = CurrentUser;
  const rez = project.value.Admins.find((el) => el.Id == x.Id);
  return rez ? true : false;
});
const isOwner = computed(() => {
  const x = CurrentUser;
  const rez = x?.Roles.includes(2);
  return rez ? true : false;
});
/**Админ хоть в 1 неймспейсе */
const isNamespaceAdmin = computed(() =>
  sidebarItems.value.some((item) => item.isNamespace() && item.IsAdminNamespace),
);

onBeforeRouteUpdate((to) => {
  if (project.value.Id === Number(to.params?.projectId)) {
    openMenuItemFromRoute(to);
  }
});
</script>

<template>
  <div>
    <div
      class="sidebar-wrapper"
      :class="{ static: isStatic }"
      @mouseover="toggleSidebarBtnVisibility(true)"
      @mouseout="toggleSidebarBtnVisibility(false)"
    >
      <div
        class="sidebar"
        :class="{ sidebar_open: isSidebarOpen, static: isStatic }"
        :style="{
          width: getRem(sidebarWidth) + 'rem',
        }"
      >
        <SidebarSkeleton v-if="isLoading" />
        <div class="sidebar__block" v-else>
          <div
            class="sidebar__open-btn"
            :class="{ 'sidebar__open-btn_open-sidebar': isSidebarOpen }"
            :style="{ left: getRem(sidebarWidth - 24) + 1.25 + 'rem' }"
            @click="toggleSidebar(!isSidebarOpen)"
          >
            <TirPmButton
              class="sidebar__button-arrow"
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Small"
            >
              <template #leftSlot>
                <ArrowRightIcon style="width: 1rem; height: 1rem" />
              </template>
            </TirPmButton>
          </div>
          <div v-if="!hideProjectInfo" class="sidebar__project-info">
            <div
              class="sidebar__project-title"
              :class="{ 'menu__wrapper_open-sidebar': isSidebarOpen }"
              @click="openProject"
            >
              <div class="sidebar__project-icon-wrap">
                <TirPmIconView
                  :size="48"
                  isRounded
                  :isBordered="false"
                  :imageLoadUrl="project.ImageUrl"
                  :colorCode="project.PreviewColor"
                >
                  <template #mainSlot>
                    {{ project?.Name[0].toUpperCase() }}
                  </template>
                </TirPmIconView>
              </div>
              <div
                class="menu__title_hidden"
                :class="{
                  menu__title_visible: (isSidebarOpen && !showOnlyIcons) || !project.ImageUrl,
                }"
              >
                {{ project.Name }}
              </div>
            </div>

            <div class="sidebar__project-line" />
          </div>
          <div
            v-if="!isStatic"
            class="menu__items"
            :class="{ 'menu__items--close': !isSidebarOpen }"
          >
            <MonitoringMenu
              v-if="isProjectAdmin || isOwner"
              :isSidebarOpen="isSidebarOpen"
              :showOnlyIcons="showOnlyIcons"
            />

            <CreateNamespaceDrawer :project-id="project.Id" v-if="isProjectAdmin || isOwner">
              <template #default="{ open }">
                <div
                  class="sidebar__menu-add menu__wrapper menu__item"
                  :class="{ 'menu__wrapper_open-sidebar': isSidebarOpen }"
                  @click="open"
                >
                  <PlusIcon
                    class="menu__icon"
                    :class="{ 'menu__icon_open-sidebar': isSidebarOpen }"
                  />
                  <div
                    class="menu__title_hidden"
                    :class="{ menu__title_visible: isSidebarOpen && !showOnlyIcons }"
                  >
                    Рабочая область
                  </div>
                </div>
              </template>
            </CreateNamespaceDrawer>
          </div>

          <TirPmScrollbar class="sidebar-menu-scroll" :show-on-hover="true">
            <div v-if="sidebarItems?.length" class="menu__items">
              <SidebarMenuItem
                v-for="(item, idx) in sidebarItems"
                :key="idx"
                :menu-item="item"
                :is-sidebar-open="isSidebarOpen || isStatic"
                :level="0"
                :sidebarWidth="sidebarWidth"
                :showOnlyIcons="showOnlyIcons && !isStatic"
                :isStatic="isStatic"
                :have-action-item="haveActionItem"
                @on-move="onMove"
                @unable-delete-queue="openRemoveStopperModal"
                @unable-delete-group="openRemoveStopperModal"
                @unable-delete-namespace="openRemoveStopperModal"
                @open-namespace="openNamespace"
                @open-group="openGroup"
                @open-queue="openQueue"
                @update:have-action-item="haveActionItem = $event"
              />
            </div>
          </TirPmScrollbar>
        </div>
      </div>

      <div v-if="!isStatic" class="sidebar__line-wrapper" @mousedown="startResize($event)">
        <div class="sidebar__line" />
      </div>
    </div>
    <div
      class="sidebar-block-width"
      :style="{
        width: getRem(sidebarWidth) + 'rem',
      }"
    ></div>
  </div>
  <RemoveStopperModal ref="$removeStopperModal" />
</template>

<style scoped lang="scss">
.sidebar__button-arrow {
  border-radius: 1rem;
}
.sidebar-block-width {
  transition: 0.2s width;
}

.sidebar__block {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar__menu-link-tasks {
  width: max-content;
  display: flex;
  align-items: center;
  padding: 0.25rem;
  transition: padding 0.2s;
  cursor: pointer;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: rgba(39, 45, 55, 0.05);
    transition: background-color 0.2s;
    border-radius: 0.375rem;
  }
}

.sidebar__menu-link-tasks--open {
  width: 100%;
  padding: 0.25rem 0.375rem 0.25rem 0.75rem;
  transition: padding 0.2s;
}

.menu__title {
  color: var(--text-primary, #272d37);
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 0.375rem;
}

.menu__items {
  padding: 0 1.25rem 0 1.25rem;
  overflow: hidden;
  flex-shrink: 0;
}

.sidebar__menu-icon-block {
  background: var(--bg-secondary, #f8f6f9);
  border-radius: 0.625rem;
  height: 1.5rem;
}

.sidebar__menu-icon {
  width: 1.5rem;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0.125rem;
}

.sidebar-wrapper:not(.static) {
  display: flex;
  position: fixed;
  top: 3.5rem;
  bottom: 0;
  left: 0;
  z-index: 1000;
}

.sidebar__line-wrapper {
  border-top: 0.063rem solid #eaeaeb;
  border-bottom: 0.063rem solid #eaeaeb;
  height: 100%;
  width: 0.188rem;
  min-height: calc(100vh - 3.5rem);
  border-right: 1px solid var(--neutral-10, #eaeaeb);
  background: var(--neutral-translucent-10, rgba(39, 45, 55, 0.1));
  cursor: ew-resize;
}

.sidebar__line {
  min-height: calc(100vh - 3.5rem);
  height: 100%;
  width: 0.1875rem;
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 4.5rem;
  background: var(--neutral-10, rgba(39, 45, 55, 0.1));
  border-top: 0.063rem solid #eaeaeb;
  border-bottom: 0.063rem solid #eaeaeb;
  transition: 0.2s width;
  z-index: 100;
  &.static {
    padding: 0;
  }
}

.sidebar__project-info {
  position: relative;
  padding: 1rem 1.25rem 0 1.25rem;
}

.sidebar__open-btn {
  position: absolute;
  width: 2rem;
  height: 2rem;
  border-radius: 100%;
  background: rgba(39, 45, 55, 0.1);
  top: 4rem;
  z-index: 110;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: 0.3s all;
}

.sidebar__open-btn_visible {
  transition: 0.3s all;
  opacity: 1;
}

.sidebar__open-btn_open-sidebar {
  transform: scaleX(-1);
  left: 21.813rem;
  transition: 0.3s all;
}

.sidebar_open {
  width: 23.5rem;
  transition: 0.3s width;
}

.menu__wrapper {
  display: flex;
  cursor: pointer;
  height: 1.5rem;
  min-height: 2rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: var(--text-primary, #272d37);
  transition: background-color 0.2s;
}

.sidebar__menu-add {
  margin-bottom: 0.5rem;
  &.menu__wrapper_open-sidebar:hover {
    background-color: rgba(39, 45, 55, 0.05);
    transition: background-color 0.2s;
  }
}

.menu__item {
  border-radius: 0.375rem;
}

.menu__item:hover {
  background-color: rgba(39, 45, 55, 0.05);
  transition: background-color 0.2s;
}

.menu__item_open {
  margin: 0;
}

.sidebar__project-title {
  min-height: 3rem;
  font-size: 0.75rem;
  line-height: 1rem;
  // color: #686c73;
  color: var(--neutral-30, #686c73);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 0.75rem;

  &.menu__wrapper_open-sidebar {
    .sidebar__project-icon-wrap {
      width: 3rem;
    }
  }
}

.sidebar__project-icon-wrap {
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  transition: width 0.2s ease;
}

.menu__wrapper_open-sidebar {
  width: 100%;
  align-items: center;
  transition: 0s align-items;

  .menu__icon {
    padding: 0.375rem 0.375rem 0.375rem 0.75rem;
    width: 2.25rem;
  }
}

.menu__icon {
  padding: 0.5rem;
  height: 2rem;
  width: 2rem;

  transition: padding 0.2s ease, width 0.5s ease;
}

.sidebar__project-icon {
  width: 3rem;
  height: 3rem;
}

.sidebar__project-line {
  height: 0.063rem;
  background-color: #bfc0c3;
  width: 100%;
  margin-bottom: 0.75rem;
}

.menu__title_hidden {
  width: 0;
  opacity: 0;
  height: 1.5rem;
  transform: translateX(-10%);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: width 0s, opacity 0s, transform 0s, height 0s;
}

.menu__title_visible {
  width: 100%;
  opacity: 1;
  height: fit-content;
  transform: translateX(0);
  color: var(--text-primary, #272d37);
  font-size: 0.87rem;
  transition: width 0.3s ease, opacity 0.2s ease 0.3s, transform 0.2s ease 0.3s, height 0s ease 0.3s;
}
.sidebar-menu-scroll {
  :deep(.hide-scroll) {
    height: 100%;
  }
  padding-bottom: 0.5rem;
}
</style>
